import CreateAxiosInstance from './axiosConfig';
const APIClient = CreateAxiosInstance(import.meta.env.VITE_API_BASE_URL);

/**
 * Ensure share-page metadata is populated (description, thumbnail, teacher_guide, tags).
 * Mirrors the share landing page's usePacketBackfill flow. Lambda-backed thumbnail
 * generation can be a few seconds first time; cached after. Anonymous-accessible.
 */
export async function ensurePacketShareMetadata(
  packetId,
  fields = ['thumbnail'],
  customErrorHandler
) {
  return await APIClient.post(
    `/packet/${packetId}/ensure`,
    { fields },
    {
      headers: { 'Content-Type': 'application/json' },
      defaultErrorMessage: null,
      // Callers looping over many packets pass a handler so one failure is reported
      // inline instead of raising the global error modal. Omitting it keeps the
      // single-packet behaviour of surfacing the failure globally.
      customErrorHandler: customErrorHandler || null,
    }
  );
}

export async function downloadPacketPDF(
  userId,
  packetId,
  packetPageUrl,
  blockUniqueIds,
  inline = false
) {
  return await APIClient.post(`/user/${userId}/packet/${packetId}/open/pdf`, {
    packet_url: packetPageUrl,
    block_unique_ids: blockUniqueIds || null,
    inline,
  });
}

export async function createPacket(form, customErrorHandler) {
  return await APIClient.post('/packet', form, {
    headers: {
      'Content-Type': 'application/json',
    },
    customErrorHandler: customErrorHandler || null,
  });
}

export async function relevelPacket(packetInfo) {
  return await APIClient.post('/packet', packetInfo, {
    headers: {
      'Content-Type': 'application/json',
    },
    defaultErrorMessage: 'There was an error releveling your resource. Please try again later.',
  });
}

export async function swapActivityBlock(packetId, blockUniqueId, canonicalId) {
  return await APIClient.patch(
    '/packet/' + packetId,
    {
      current_block: blockUniqueId,
      replacement_block: canonicalId,
      operation: 'replace_block',
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      defaultErrorMessage: 'There was an error swapping your activity. Please try again later.',
    }
  );
}

export async function addItemsToBlock(packetId, blockId, itemsToAdd = []) {
  return await APIClient.patch(
    '/packet/' + packetId,
    {
      unique_id: blockId,
      operation: 'add_items_to_block',
      items_to_add: itemsToAdd,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      defaultErrorMessage: 'There was an error adding items to your block. Please try again later.',
    }
  );
}

export async function addActivityBlock(packetId, canonicalId) {
  return await APIClient.patch(
    '/packet/' + packetId,
    {
      canonical_id: canonicalId,
      operation: 'add_block',
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
}
export async function getPacket(packetId) {
  return await APIClient.get('/packet/' + packetId, {
    defaultErrorMessage: 'There was an error getting your resource. Please try again later.',
  });
}

export async function getPackets(
  search,
  page,
  pageSize,
  onlyFavorites = false,
  collectionIds = [],
  // Lets a caller that handles its own failures suppress the global error dialog.
  options = {}
) {
  // per_page, not page_size: the endpoint declares per_page (packet.py) and drops anything
  // else, so page_size silently left every caller on the default of 10.
  let url = '/packet?search=' + search + '&page=' + page + '&per_page=' + pageSize;
  if (onlyFavorites) {
    url += '&favorites=true';
  }
  if (collectionIds.length > 0) {
    url += '&collection_ids=' + collectionIds.join(',');
  }
  return await APIClient.get(url, {
    defaultErrorMessage: 'There was an error getting your resources. Please try again later.',
    ...options,
  });
}

export async function getUnlimitedPromptPackets(perPage = 30) {
  return await APIClient.get(
    '/packet?source=unlimited&include=input_flags_json&per_page=' + perPage,
    {
      defaultErrorMessage: 'There was an error loading your prompt history.',
    }
  );
}

export async function deletePacket(packetId) {
  return await APIClient.delete('/packet/' + packetId, {
    defaultErrorMessage: 'There was an error deleting your resource. Please try again later.',
  });
}

export async function updatePacket(packetId, fields) {
  return await APIClient.put('/packet/' + packetId, fields, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function generateSections(packetId, { section_type, count = 5 }) {
  const response = await APIClient.post('/packet/' + packetId + '/generate-sections', {
    section_type,
    count,
  });
  return response.data?.sections ?? [];
}

export async function customizePacket(
  packetId,
  blockUniqueId,
  blockType,
  adjustment,
  readingLevelId,
  language
) {
  return await APIClient.patch(
    `/packet/${packetId}`,
    {
      unique_block_id: blockUniqueId,
      block_type: blockType,
      adjustment: adjustment,
      reading_level_id: readingLevelId,
      language: language,
      operation: 'customize_packet',
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      defaultErrorMessage: 'There was an error customizing the block. Please try again later.',
    }
  );
}

export async function createTranslatedPacket(packet, language, resourceTypes) {
  let packetCopy = { ...packet };
  return await APIClient.post(
    '/packet?translate=true',
    {
      packet: packetCopy,
      resources: resourceTypes,
      language: language,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
}

export async function updatePacketSkills(packetId, skills) {
  return await APIClient.patch(
    '/packet/' + packetId + '/skills',
    {
      skills: skills,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
}

export async function applyTemplateToPacket(templateId, packetId, projectId) {
  return await APIClient.post(
    '/packet',
    {
      template_id: templateId,
      packet_id: packetId,
      apply_template: true,
      ...(projectId && { project_id: projectId }),
    },
    {
      headers: { 'Content-Type': 'application/json' },
    }
  );
}

export async function updatedPacketWithAllowedCitations(packet, allowedCitations) {
  return await APIClient.post(
    '/packet?regenerate=true',
    {
      packet: { ...packet },
      allowedCitations: allowedCitations,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
}

export function getLanguageStandardsFromPacket(packet) {
  let languageStandards = new Set();
  let generatedContent = [];
  if (packet.questions_json) {
    let resource = packet.questions_json;
    const { questions } = resource;
    // Go through each question. If there is a skill_json object underneath it, add its id as the key to the set and set its value to the skill_json
    questions.forEach((question) => {
      if (question.skill_json) {
        const { id } = question.skill_json;
        generatedContent.push(question);
        languageStandards.add(id);
      }
    });
  }

  if (packet.short_json) {
    let resource = packet.short_json;
    const { prompts } = resource;
    prompts.forEach((prompt) => {
      if (prompt.skill_json) {
        const { id } = prompt.skill_json;
        generatedContent.push(prompt);
        languageStandards.add(id);
      }
    });
  }

  let output = Array.from(languageStandards)
    .map((standard) => {
      const standardObj = generatedContent.find((content) => content.skill_json.id === standard);
      return standardObj.skill_json;
    })
    .filter((standard) => standard.framework !== 'Depth of Knowledge');

  // Return the set as an array
  return output;
}

export async function getTradebooksJson() {
  try {
    // Get the tradebooks directly from S3 instead of using the API
    const response = await fetch('https://books.diffit.me/metadata.json');

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Data is bytes json, so we need to parse it
    const data = JSON.parse(await response.text());
    return { data };
  } catch (error) {
    console.error('Error fetching tradebooks from S3:', error);
    throw error;
  }
}

export async function togglePacketFavorite(packetId, newFavoriteState) {
  return await APIClient.post(
    '/packet/' + packetId + '/favorite',
    {
      is_favorite: newFavoriteState,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
}

export async function sharePacketWithColleagues(packetId, colleagues, projectId) {
  return await APIClient.post(
    `/packet/${packetId}/share`,
    {
      colleagues: colleagues,
      ...(projectId && { project_id: projectId }),
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      defaultErrorMessage: 'There was an error sharing your packet. Please try again later.',
    }
  );
}

/**
 * Mail the signed-in owner a link back to their own resource (mobile can't edit one).
 * Owner-only on the backend, and the recipient is always the caller's own address —
 * this sends no email of its own choosing.
 */
export async function emailPacketToSelf(packetId, projectId) {
  return await APIClient.post(
    `/packet/${packetId}/email-to-me`,
    {
      ...(projectId && { project_id: projectId }),
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      defaultErrorMessage: 'There was an error emailing your resource. Please try again later.',
    }
  );
}

export async function getCollectionsForPacket(packetId) {
  return await APIClient.get('/packet/' + packetId + '/collections', {
    defaultErrorMessage: 'There was an error getting your collections. Please try again later.',
  });
}

/**
 * Trigger the one-time creator share-notification email (RESOURCE_PUBLISHED
 * Customer.io transactional template). Idempotent on the backend — only the
 * first call fires the email; subsequent calls 204 without sending.
 *
 * Routes through the unified POST /packet/{id}/analytics endpoint with
 * action="notify_creator_shared" (formerly its own /notify-creator-shared
 * route). Fire-and-forget — callers should `.catch(() => {})`.
 *
 * `landingVariant` is always "email_only"; the backend stamps it onto the
 * email's share_url so share-page Mixpanel events can attribute the source.
 *
 * The no-op `customErrorHandler` is what actually makes this fire-and-forget: the
 * axios response interceptor pops the global error modal for any request that
 * supplies no handler, so a caller's `.catch(() => {})` runs too late to stop it.
 * This is a background side effect the teacher can neither cause nor fix, so its
 * failures must stay silent.
 */
export async function notifyCreatorShared(packetId, landingVariant) {
  return await APIClient.post(
    `/packet/${packetId}/analytics`,
    {
      action: 'notify_creator_shared',
      landing_variant: landingVariant,
    },
    { customErrorHandler: () => {} }
  );
}
